import React, { useState, useEffect, useCallback } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { makeStyles } from "@material-ui/core/styles";
import {
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    Grid,
    Typography,
    Checkbox,
    FormGroup
} from "@material-ui/core";
import ScheduleExceptionService from "../../services/ScheduleExceptionService";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { DeleteOutline, AddCircleOutline } from "@material-ui/icons";
import { IconButton } from "@material-ui/core";

const localizer = momentLocalizer(moment);

const useStyles = makeStyles((theme) => ({
    root: {
        height: 600,
        padding: theme.spacing(2),
    },
    calendar: {
        height: "100%",
    },
    actionModal: {
        minWidth: 400
    }
}));

const ProfessionalAvailabilityTab = ({ professionalId }) => {
    const classes = useStyles();
    const [exceptions, setExceptions] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [actionType, setActionType] = useState("standard"); // standard, blocked, available
    const [customSchedules, setCustomSchedules] = useState([{ startTime: "08:00", endTime: "18:00" }]);

    // Bulk State
    const [openBulkDialog, setOpenBulkDialog] = useState(false);
    const [bulkConfig, setBulkConfig] = useState({
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().add(1, 'month').format("YYYY-MM-DD"),
        schedules: [{ startTime: "08:00", endTime: "18:00" }],
        weekdays: [], // Start empty to force explicit selection
        type: 'available'
    });

    const fetchExceptions = useCallback(async () => {
        if (!professionalId) return;
        try {
            const data = await ScheduleExceptionService.list({ professionalId });
            setExceptions(data);
        } catch (err) {
            toast.error(i18n.t("toastError.load"));
        }
    }, [professionalId]);

    useEffect(() => {
        fetchExceptions();
    }, [fetchExceptions]);

    const handleSelectSlot = (slotInfo) => {
        const dateStr = moment(slotInfo.start).format("YYYY-MM-DD");

        // Find ALL exceptions for this date
        const dateExceptions = exceptions.filter(e => moment(e.date).format("YYYY-MM-DD") === dateStr);

        setSelectedDate(slotInfo.start);

        if (dateExceptions.length > 0) {
            // Assume all have the same type, check the first one
            const type = dateExceptions[0].type;
            setActionType(type);

            if (type === 'available') {
                const schedules = dateExceptions.map(e => ({
                    startTime: e.startTime,
                    endTime: e.endTime
                }));
                // Sort by start time for better UX
                schedules.sort((a, b) => a.startTime.localeCompare(b.startTime));
                setCustomSchedules(schedules);
            } else {
                // Reset to default if blocked
                setCustomSchedules([{ startTime: "08:00", endTime: "18:00" }]);
            }
        } else {
            setActionType("standard");
            setCustomSchedules([{ startTime: "08:00", endTime: "18:00" }]);
        }

        setOpenDialog(true);
    };

    const handleSave = async () => {
        try {
            const dateStr = moment(selectedDate).format("YYYY-MM-DD");

            if (actionType === "standard") {
                // Determine if we need to delete an existing exception
                const dateExceptions = exceptions.filter(e => moment(e.date).format("YYYY-MM-DD") === dateStr);
                for (const exception of dateExceptions) {
                    await ScheduleExceptionService.delete(exception.id);
                }
            } else {
                // For 'available' type, we send the schedules array
                // For 'blocked', we send empty schedules or just type
                const payload = {
                    professionalId,
                    date: dateStr,
                    type: actionType,
                };

                if (actionType === 'available') {
                    payload.schedules = customSchedules;
                }

                await ScheduleExceptionService.save(payload);
            }

            toast.success(i18n.t("common.success"));
            setOpenDialog(false);
            fetchExceptions();
        } catch (err) {
            toast.error(i18n.t("toastError.save"));
        }
    };

    const handleBulkSave = async () => {
        if (bulkConfig.weekdays.length === 0) {
            toast.warn("Seleccione al menos un día de la semana");
            return;
        }

        try {
            if (!professionalId) return;
            // Destructure to separate schedules from other config if needed, or pass it all
            const payload = {
                professionalId,
                startDate: bulkConfig.startDate,
                endDate: bulkConfig.endDate,
                weekdays: bulkConfig.weekdays,
                type: bulkConfig.type,
            };

            if (bulkConfig.type === 'available') {
                payload.schedules = bulkConfig.schedules;
            }

            await ScheduleExceptionService.bulkSave(payload);
            toast.success(i18n.t("common.success"));
            setOpenBulkDialog(false);
            fetchExceptions();
        } catch (err) {
            toast.error("Error saving bulk exceptions");
        }
    };

    const handleWeekdayChange = (dayIndex) => {
        const currentDays = [...bulkConfig.weekdays];
        if (currentDays.includes(dayIndex)) {
            setBulkConfig({ ...bulkConfig, weekdays: currentDays.filter(d => d !== dayIndex) });
        } else {
            setBulkConfig({ ...bulkConfig, weekdays: [...currentDays, dayIndex] });
        }
    };

    const handleSelectWeekdays = (type) => {
        if (type === 'all') {
            setBulkConfig({ ...bulkConfig, weekdays: [0, 1, 2, 3, 4, 5, 6] });
        } else if (type === 'workdays') {
            setBulkConfig({ ...bulkConfig, weekdays: [1, 2, 3, 4, 5] });
        } else if (type === 'none') {
            setBulkConfig({ ...bulkConfig, weekdays: [] });
        }
    };

    const handleCustomScheduleChange = (index, field, value) => {
        const newSchedules = [...customSchedules];
        newSchedules[index][field] = value;
        setCustomSchedules(newSchedules);
    };

    const addCustomSchedule = () => {
        setCustomSchedules([...customSchedules, { startTime: "08:00", endTime: "18:00" }]);
    };

    const removeCustomSchedule = (index) => {
        const newSchedules = customSchedules.filter((_, i) => i !== index);
        setCustomSchedules(newSchedules);
    };

    const handleBulkScheduleChange = (index, field, value) => {
        const newSchedules = [...bulkConfig.schedules];
        newSchedules[index][field] = value;
        setBulkConfig({ ...bulkConfig, schedules: newSchedules });
    };

    const addBulkSchedule = () => {
        setBulkConfig({ ...bulkConfig, schedules: [...bulkConfig.schedules, { startTime: "08:00", endTime: "18:00" }] });
    };

    const removeBulkSchedule = (index) => {
        const newSchedules = bulkConfig.schedules.filter((_, i) => i !== index);
        setBulkConfig({ ...bulkConfig, schedules: newSchedules });
    };

    const daysOfWeek = [
        { id: 1, label: 'Lun' },
        { id: 2, label: 'Mar' },
        { id: 3, label: 'Mié' },
        { id: 4, label: 'Jue' },
        { id: 5, label: 'Vie' },
        { id: 6, label: 'Sáb' },
        { id: 0, label: 'Dom' },
    ];

    const events = exceptions.map(e => {
        let title = "";
        if (e.type === 'blocked') {
            title = "Bloqueado";
        } else if (e.type === 'available') {
            title = `${e.startTime} - ${e.endTime}`;
        }

        const date = moment(e.date).add(12, 'hours').toDate();

        return {
            title,
            start: date,
            end: date,
            allDay: true,
            resource: e
        };
    });

    const eventPropGetter = (event) => {
        const type = event.resource.type;
        if (type === 'blocked') {
            return {
                style: {
                    backgroundColor: '#e57373', // Red
                    color: 'white'
                }
            };
        }
        if (type === 'available') {
            return {
                style: {
                    backgroundColor: '#81c784', // Green
                    color: 'white'
                }
            };
        }
        return {};
    };

    return (
        <Paper className={classes.root}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setOpenBulkDialog(true)}
                    disabled={!professionalId}
                >
                    Gestión Masiva
                </Button>
            </div>

            <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 500 }}
                views={['month']}
                popup
                popupOffset={{ x: 0, y: 20 }}
                selectable
                onSelectSlot={handleSelectSlot}
                onSelectEvent={(event) => handleSelectSlot({ start: event.start })}
                eventPropGetter={eventPropGetter}
                messages={{
                    today: i18n.t("calendar.today") || "Hoy",
                    previous: i18n.t("calendar.back") || "Atrás",
                    next: i18n.t("calendar.next") || "Siguiente",
                    month: i18n.t("calendar.month") || "Mes",
                    week: i18n.t("calendar.week") || "Semana",
                    day: i18n.t("calendar.day") || "Día",
                    showMore: total => `+${total} más`
                }}
            />

            {/* ... Custom Day Dialog ... */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>
                    Configurar Disponibilidad - {selectedDate && moment(selectedDate).format("DD/MM/YYYY")}
                </DialogTitle>
                <DialogContent className={classes.actionModal}>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={actionType}
                            onChange={(e) => setActionType(e.target.value)}
                        >
                            <FormControlLabel
                                value="standard"
                                control={<Radio />}
                                label="Usar Horario Estándar"
                            />
                            <FormControlLabel
                                value="blocked"
                                control={<Radio />}
                                label="Bloquear Día (No Disponible)"
                            />
                            <FormControlLabel
                                value="available"
                                control={<Radio />}
                                label="Horario Personalizado"
                            />
                        </RadioGroup>
                    </FormControl>

                    {actionType === 'available' && (
                        <Grid container spacing={2} style={{ marginTop: 16 }}>
                            {customSchedules.map((schedule, index) => (
                                <React.Fragment key={index}>
                                    <Grid item xs={5}>
                                        <TextField
                                            label="Hora Inicio"
                                            type="time"
                                            value={schedule.startTime}
                                            onChange={(e) => handleCustomScheduleChange(index, 'startTime', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ step: 300 }}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={5}>
                                        <TextField
                                            label="Hora Fin"
                                            type="time"
                                            value={schedule.endTime}
                                            onChange={(e) => handleCustomScheduleChange(index, 'endTime', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ step: 300 }}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
                                        <IconButton onClick={() => removeCustomSchedule(index)} disabled={customSchedules.length === 1}>
                                            <DeleteOutline />
                                        </IconButton>
                                    </Grid>
                                </React.Fragment>
                            ))}
                            <Grid item xs={12}>
                                <Button
                                    startIcon={<AddCircleOutline />}
                                    onClick={addCustomSchedule}
                                    color="primary"
                                >
                                    Agregar Horario
                                </Button>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary">
                                    Este horario sobrescribirá el horario estándar para este día.
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)} color="default">
                        Cancelar
                    </Button>
                    <Button onClick={handleSave} color="primary" variant="contained">
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Dialog */}
            <Dialog open={openBulkDialog} onClose={() => setOpenBulkDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Gestión Masiva de Disponibilidad</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl component="fieldset">
                                <Typography variant="subtitle2" gutterBottom>Acción a Realizar:</Typography>
                                <RadioGroup
                                    row
                                    value={bulkConfig.type}
                                    onChange={(e) => setBulkConfig({ ...bulkConfig, type: e.target.value })}
                                >
                                    <FormControlLabel
                                        value="available"
                                        control={<Radio color="primary" />}
                                        label="Habilitar (Disponibilidad)"
                                    />
                                    <FormControlLabel
                                        value="blocked"
                                        control={<Radio color="secondary" />}
                                        label="Bloquear (No Disponible)"
                                    />
                                    <FormControlLabel
                                        value="standard"
                                        control={<Radio />}
                                        label="Limpiar (Borrar Excepciones)"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                label="Fecha Inicio"
                                type="date"
                                value={bulkConfig.startDate}
                                onChange={(e) => setBulkConfig({ ...bulkConfig, startDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Fecha Fin"
                                type="date"
                                value={bulkConfig.endDate}
                                onChange={(e) => setBulkConfig({ ...bulkConfig, endDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>

                        {bulkConfig.type === 'available' && (
                            <>
                                {bulkConfig.schedules.map((schedule, index) => (
                                    <React.Fragment key={index}>
                                        <Grid item xs={5}>
                                            <TextField
                                                label="Hora Inicio"
                                                type="time"
                                                value={schedule.startTime}
                                                onChange={(e) => handleBulkScheduleChange(index, 'startTime', e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ step: 300 }}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={5}>
                                            <TextField
                                                label="Hora Fin"
                                                type="time"
                                                value={schedule.endTime}
                                                onChange={(e) => handleBulkScheduleChange(index, 'endTime', e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ step: 300 }}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
                                            <IconButton onClick={() => removeBulkSchedule(index)} disabled={bulkConfig.schedules.length === 1}>
                                                <DeleteOutline />
                                            </IconButton>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                                <Grid item xs={12}>
                                    <Button
                                        startIcon={<AddCircleOutline />}
                                        onClick={addBulkSchedule}
                                        color="primary"
                                    >
                                        Agregar Horario
                                    </Button>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" gutterBottom>Aplicar a días de la semana:</Typography>
                                <div>
                                    <Button size="small" onClick={() => handleSelectWeekdays('all')}>Todos</Button>
                                    <Button size="small" onClick={() => handleSelectWeekdays('workdays')}>Lun-Vie</Button>
                                    <Button size="small" onClick={() => handleSelectWeekdays('none')}>Ninguno</Button>
                                </div>
                            </div>
                            <FormGroup row>
                                {daysOfWeek.map((day) => (
                                    <FormControlLabel
                                        key={day.id}
                                        control={
                                            <Checkbox
                                                checked={bulkConfig.weekdays.includes(day.id)}
                                                onChange={() => handleWeekdayChange(day.id)}
                                                color="primary"
                                            />
                                        }
                                        label={day.label}
                                    />
                                ))}
                            </FormGroup>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenBulkDialog(false)} color="default">
                        Cancelar
                    </Button>
                    <Button onClick={handleBulkSave} color="primary" variant="contained">
                        Aplicar Cambios
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default ProfessionalAvailabilityTab;
